import React, { useEffect, useState } from 'react';
import { Table, Spin, message, Button, Tooltip } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';  // Import the Google Maps icon
import axios from '../../../config/axiosInstance';

const HotelBookingsTab = ({ participantId, host }) => {
    const [hotelBookings, setHotelBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch Hotel Bookings data from the backend
    const fetchHotelBookings = async () => {
        try {
            const tokenRes = await axios.get(`/regasus/token?host=${host}`);
            const token = tokenRes.data.access_token;

            if (!token) {
                message.error('No token received');
                return;
            }

            const res = await axios.get(`/regasus/participants/${participantId}/hotelBookings-new`, {
                params: {
                    token,
                    host,
                }
            });

            setHotelBookings(res.data?.hotelBookings || []);
        } catch (err) {
            console.error(err);
            message.error('Failed to load hotel bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHotelBookings();
    }, [participantId, host]);

    // Function to get the description based on language preference
    const getDescription = (descriptions) => {
        const enDescription = descriptions.find(desc => desc.lang === 'en');
        return enDescription ? enDescription.value : descriptions[0]?.value || 'No description available';
    };

    // Function to get the hotel website
    const getHotelWebsite = (hotel) => {
        const rawUrl = hotel?.communication?.www || '';
        if (!rawUrl) return '#';

        // If it already starts with http or https, we return it as is.
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;

        // If not, we add https:// by default
        return `https://${rawUrl}`;
    };

    // Function to create Google Maps link
    const getGoogleMapsLink = (address) => {
        const { street, zip, city, country } = address || {};
        if (street && zip && city && country) {
            const addressStr = `${street}, ${zip}, ${city}, ${country}`;
            return `https://www.google.com/maps?q=${encodeURIComponent(addressStr)}`;
        }
        return '#';
    };

    // Expandable row configuration to show details for each booking
    const expandedRowRender = (record) => {
        const {
            lodgePrice = { amountBrutto: 0, currency: 'EUR' },
            breakfastPrice = { amountBrutto: 0, currency: 'EUR' },
            additionalPrice1 = { amountBrutto: 0, currency: 'EUR' },
            additionalPrice2 = { amountBrutto: 0, currency: 'EUR' },
            cancelFeePrice = { amountBrutto: 0, currency: 'EUR' },
            hotelOffering = {}
        } = record;

        // Get description in the preferred language
        const description = hotelOffering?.description ? getDescription(hotelOffering.description) : 'No description available';

        // Calculate the total price (sum of all amountBrutto)
        const totalPrice = lodgePrice.amountBrutto + breakfastPrice.amountBrutto + additionalPrice1.amountBrutto + additionalPrice2.amountBrutto + cancelFeePrice.amountBrutto;

        return (
            <div>
                <Table
                    size="small"
                    pagination={false}
                    dataSource={[
                        { key: '1', label: 'Room', value: lodgePrice.amountBrutto, currency: lodgePrice.currency },
                        { key: '2', label: 'Breakfast Price', value: breakfastPrice.amountBrutto, currency: breakfastPrice.currency },
                        { key: '3', label: 'City Tax', value: additionalPrice1.amountBrutto, currency: additionalPrice1.currency },
                        { key: '4', label: 'Cancel Fee Price', value: cancelFeePrice.amountBrutto, currency: cancelFeePrice.currency },
                        { key: '5', label: 'Total Price', value: totalPrice, currency: lodgePrice.currency },
                    ]}
                    columns={[
                        {
                            title: 'Description',
                            dataIndex: 'label',
                            key: 'label',
                            render: (label, record) => {
                                // If this is the "Total Price" row, make the label bold and larger
                                const style = record.key === '5' ? { fontWeight: 'bold', fontSize: '18px' } : {};
                                return (
                                    <span style={style}>
                                {label}
                            </span>
                                );
                            },
                        },
                        {
                            title: 'Amount',
                            dataIndex: 'value',
                            key: 'value',
                            render: (value, record) => {
                                // If this is the "Total Price" row, make it bold and larger
                                const style = record.key === '5' ? { fontWeight: 'bold', fontSize: '18px' } : {};
                                return (
                                    <span style={style}>
                                {value} {record.currency}
                            </span>
                                );
                            },
                        },
                    ]}
                    rowKey="key"
                />
            </div>
        );
    };

    // Table columns configuration
    const columns = [
        {
            title: 'Hotel Location',
            dataIndex: 'hotel',
            key: 'hotel',
            render: (hotel) => {
                const googleMapsLink = getGoogleMapsLink(hotel?.address1);
                const hotelWebsite = getHotelWebsite(hotel);
                return (
                    <span>
                        <a href={googleMapsLink} target="_blank" rel="noopener noreferrer">
                            <Tooltip title="View on Google Maps">
                                 <Button icon={<EnvironmentOutlined />} type="link" style={{ color: 'red' }}/>
                            </Tooltip>
                        </a>
                        <span style={{ marginLeft: 8 }}>
                            <a href={hotelWebsite} target="_blank" rel="noopener noreferrer">
                                {hotel?.name1 || 'No location available'}
                            </a>
                        </span>
                    </span>
                );
            }
        },
        {
            title: 'Hotel Offering',
            dataIndex: 'hotelOffering',
            key: 'hotelOffering',
            render: (_, record) => {
                return record.hotelOffering?.description ? getDescription(record.hotelOffering.description) : 'No description available';
            }
        },
        {
            title: 'Arrival Date',
            dataIndex: 'arrival',
            key: 'arrival',
        },
        {
            title: 'Departure Date',
            dataIndex: 'departure',
            key: 'departure',
        },
        {
            title: 'Benefit Recipient',
            dataIndex: 'benefitRecipientName',
            key: 'benefitRecipientName',
            render: (benefitRecipientName) => benefitRecipientName,
        },
        {
            title: 'Invoice Recipient',
            dataIndex: 'invoiceRecipientName',
            key: 'invoiceRecipientName',
            render: (invoiceRecipientName) => invoiceRecipientName,
        },
    ];

    return (
        <div>
            {loading ? (
                <Spin />
            ) : (
                <Table
                    size="small"
                    dataSource={hotelBookings.map((booking, i) => ({
                        key: booking.id || i,
                        ...booking, // Include all properties of the booking
                    }))}
                    columns={columns}
                    pagination={{ pageSize: 10 }}
                    expandable={{
                        expandedRowRender: expandedRowRender, // Define how to render expanded rows
                        rowExpandable: () => true, // All rows are expandable
                    }}
                />
            )}
        </div>
    );
};

export default HotelBookingsTab;
